import * as React from 'react';
import type { PlanRefusalDto } from '@relay-towing/api';
import {
  CALL_REASONS,
  TOW_TYPES,
  formatTowTypeForDisplay,
  planRefusalOf,
} from '@relay-towing/api';
import {
  AddTile,
  BottomSheet,
  BottomSheetClose,
  BottomSheetContent,
  Button,
  Form,
  FormField,
  Grid,
  Input,
  MemberRow,
  OperatorPicker,
  OptionSheet,
  PhoneInput,
  Stack,
  Surface,
  Text,
  applyServerErrors,
  toast,
  useZodForm,
} from '@relay-towing/ui';
import { Users } from '@phosphor-icons/react';
import { useGetAllDrivers } from '../../api/hooks/useGetAllDrivers';
import { useGetCustomers } from '../../api/hooks/useGetCustomers';
import { useGetVehicleMakes } from '../../api/hooks/useGetVehicleMakes';
import { useGetVehicleModels } from '../../api/hooks/useGetVehicleModels';
import { useCreateCall } from '../../hooks/useCreateCall';
import { useDriverProfile } from '../../hooks/useDriverProfile';
import { useMember } from '../../hooks/useMember';
import {
  createCallSchema,
  generateDispatchNumber,
  toCreateCallRequest,
  type CallCoords,
  type CallType,
  type CreateCallValues,
} from '../../lib/createCallSchema';
import { RefusalUpsell } from '../plan/RefusalUpsell';
import { AddressInput } from '../shell/AddressInput';

const PRIORITIES = [
  { value: 'LOW', label: 'Low' },
  { value: 'NORMAL', label: 'Normal' },
  { value: 'HIGH', label: 'High' },
  { value: 'URGENT', label: 'Urgent' },
];

// The dashboard's words for the kind of call, which it calls its status.
const CALL_TYPE_OPTIONS: { value: CallType; label: string }[] = [
  { value: 'new', label: 'New Call' },
  { value: 'completed', label: 'Completed Call' },
  { value: 'scheduled', label: 'Schedule Call' },
];

const CREATED: Record<CallType, string> = {
  new: 'New call created',
  completed: 'Completed call created',
  scheduled: 'Scheduled call created',
};

const TOW_TYPE_OPTIONS = TOW_TYPES.map((type) => ({
  value: type,
  label: formatTowTypeForDisplay(type),
}));

const REASON_OPTIONS = CALL_REASONS.map((reason) => ({
  value: reason,
  label: reason,
}));

/** '' for an emptied box, so the schema sees nothing rather than NaN. */
const toNumberOrUndefined = (event: React.ChangeEvent<HTMLInputElement>) =>
  event.target.value === '' ? undefined : event.target.valueAsNumber;

/**
 * Creating a call on a phone, as the Figma's Creating frames draw it: one
 * sheet that scrolls through Details, Contact and Location, Vehicle and
 * Assign, with Cancel and Create at its foot, and each choice rising in its
 * own sheet over it. The whole form is checked on Create, each problem
 * under its field. The schema is the old Create Call screen's, so both ask
 * the same but for notes. It starts on the company's default account, and the member
 * creating it is on it by default when they drive. A refusal because the
 * plan's monthly calls are used up opens the plan gate with the server's
 * words; any other refusal goes under its field or at the top.
 */
export function CreateCallSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const member = useMember();
  const { data: profile } = useDriverProfile();
  const { mutate: createCall, isPending } = useCreateCall();
  const { data: customers } = useGetCustomers();
  const { data: drivers } = useGetAllDrivers();
  const { data: makes } = useGetVehicleMakes();
  const formId = React.useId();
  const [picking, setPicking] = React.useState(false);
  const [coords, setCoords] = React.useState<CallCoords>({});
  const [refusal, setRefusal] = React.useState<PlanRefusalDto | null>(null);

  const self = member.drives && profile?.userId ? [profile.userId] : [];
  const form = useZodForm(createCallSchema, {
    defaultValues: {
      account: '',
      priority: 'NORMAL',
      callType: 'new',
      towType: 'ANY',
      reason: '',
      completionTime: '',
      scheduledDate: '',
      scheduledTime: '',
      dispatchNumber: generateDispatchNumber(),
      contactName: '',
      contactPhone: '',
      pickupLocation: '',
      destination: '',
      vehicleMake: '',
      vehicleModel: '',
      vehicleYear: '',
      vehicleColor: '',
      licensePlate: '',
      vin: '',
      drivers: self,
      // Not asked for here: a note is added from the call's Notes once it
      // is made (the owner, 2026-10-27). The old shell's screen still asks.
      notes: '',
    },
  });
  // The company's default account, once the accounts have loaded and only
  // while nobody has picked one, so a call made in a hurry is on an account
  // without a trip to the picker. One the list does not offer is left
  // unpicked, rather than sent without the member seeing it.
  const defaultAccount = member.membership?.company.defaultCustomerId;
  React.useEffect(() => {
    if (!defaultAccount || form.getValues('account')) return;
    if (!customers?.data.some((customer) => customer.id === defaultAccount))
      return;
    form.setValue('account', defaultAccount);
  }, [defaultAccount, customers, form]);
  const callType = form.watch('callType');
  const make = form.watch('vehicleMake');
  const operators = form.watch('drivers');
  const { data: models } = useGetVehicleModels({ make }, { enabled: !!make });

  const operatorOptions = (drivers ?? [])
    .filter((driver) => driver.user?.id)
    .map((driver) => ({
      id: driver.user!.id,
      name: driver.name,
      detail:
        driver.user!.id === profile?.userId
          ? 'You'
          : driver.truckNumber
            ? `Truck ${driver.truckNumber}`
            : undefined,
    }));

  const chosen = operatorOptions.filter((option) =>
    operators.includes(option.id)
  );

  const submit = (values: CreateCallValues) =>
    createCall(toCreateCallRequest(values, coords), {
      // Back where the member started, as the Figma's Created frame has
      // it: the list shows the new call; Home, which cannot, says so.
      onSuccess: () => {
        onOpenChange(false);
        // Only a call to run now goes out to its operators on creating.
        toast.success(
          values.callType === 'new' && values.drivers.length
            ? 'New call created, and sent to operators'
            : CREATED[values.callType]
        );
      },
      onError: (error) => {
        const refused = planRefusalOf(error);
        if (refused) return setRefusal(refused);
        applyServerErrors(form, error, 'The call could not be created.');
      },
    });

  return (
    <>
      <BottomSheet open={open} onOpenChange={onOpenChange}>
        <BottomSheetContent
          title="Create a call"
          hideTitle
          size="full"
          footer={
            <>
              <BottomSheetClose asChild>
                <Button variant="secondary" size="xl" pill disabled={isPending}>
                  Cancel
                </Button>
              </BottomSheetClose>
              <Button
                type="submit"
                form={formId}
                size="xl"
                pill
                loading={isPending}
              >
                Create
              </Button>
            </>
          }
        >
          <Form form={form} id={formId} gap={10} onSubmit={submit}>
            <Stack gap={4}>
              <Text as="h2" variant="h2">
                Details
              </Text>
              <FormField form={form} name="account" label="Account">
                {(field) => (
                  <OptionSheet
                    title="Account"
                    searchable
                    placeholder="Select an account"
                    options={(customers?.data ?? []).map((customer) => ({
                      value: customer.id,
                      label: customer.accountName,
                    }))}
                    value={field.value || undefined}
                    onValueChange={field.onChange}
                  />
                )}
              </FormField>
              <FormField form={form} name="priority" label="Priority">
                {(field) => (
                  <OptionSheet
                    title="Priority"
                    options={PRIORITIES}
                    value={field.value}
                    onValueChange={field.onChange}
                  />
                )}
              </FormField>
              <FormField form={form} name="callType" label="Status">
                {(field) => (
                  <OptionSheet
                    title="Status"
                    options={CALL_TYPE_OPTIONS}
                    value={field.value}
                    onValueChange={field.onChange}
                  />
                )}
              </FormField>
              {callType === 'completed' ? (
                <>
                  <FormField
                    form={form}
                    name="completionTime"
                    label="Completion Time"
                  >
                    {(field) => <Input {...field} type="datetime-local" />}
                  </FormField>
                  <Grid cols={2} gap={4}>
                    <FormField form={form} name="mileage" label="Mileage">
                      {(field) => (
                        <Input
                          {...field}
                          type="number"
                          inputMode="decimal"
                          value={field.value ?? ''}
                          onChange={(event) =>
                            field.onChange(toNumberOrUndefined(event))
                          }
                        />
                      )}
                    </FormField>
                    <FormField
                      form={form}
                      name="totalCharges"
                      label="Total Charges"
                    >
                      {(field) => (
                        <Input
                          {...field}
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          value={field.value ?? ''}
                          onChange={(event) =>
                            field.onChange(toNumberOrUndefined(event))
                          }
                        />
                      )}
                    </FormField>
                  </Grid>
                </>
              ) : null}
              {callType === 'scheduled' ? (
                // Required only when scheduling, which the schema's
                // refinement says and the marker has to match.
                <Grid cols={2} gap={4}>
                  <FormField
                    form={form}
                    name="scheduledDate"
                    label="Schedule Date"
                    required
                  >
                    {(field) => <Input {...field} type="date" />}
                  </FormField>
                  <FormField
                    form={form}
                    name="scheduledTime"
                    label="Schedule Time"
                    required
                  >
                    {(field) => <Input {...field} type="time" />}
                  </FormField>
                </Grid>
              ) : null}
              <FormField form={form} name="towType" label="Tow Type">
                {(field) => (
                  <OptionSheet
                    title="Tow Type"
                    options={TOW_TYPE_OPTIONS}
                    value={field.value}
                    onValueChange={field.onChange}
                  />
                )}
              </FormField>
              <FormField form={form} name="reason" label="Reason">
                {(field) => (
                  <OptionSheet
                    title="Reason"
                    searchable
                    allowCustom
                    // Saved in capitals, as the dashboard saves one typed in.
                    formatCustomValue={(value) => value.toUpperCase()}
                    placeholder="Select a reason"
                    searchPlaceholder="Search, or type a reason"
                    options={REASON_OPTIONS}
                    value={field.value || undefined}
                    onValueChange={field.onChange}
                  />
                )}
              </FormField>
            </Stack>
            <Stack gap={4}>
              <Text as="h2" variant="h2">
                Contact and Location
              </Text>
              <FormField form={form} name="contactName" label="Contact Name">
                {(field) => <Input {...field} placeholder="Full name" />}
              </FormField>
              <FormField form={form} name="contactPhone" label="Contact Phone">
                {(field) => <PhoneInput {...field} />}
              </FormField>
              <FormField form={form} name="pickupLocation" label="Pickup">
                {(field) => (
                  <AddressInput
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Pickup address"
                    onPlace={(place) =>
                      setCoords((prev) => ({
                        ...prev,
                        pickupLocationLat: place?.lat,
                        pickupLocationLong: place?.lng,
                      }))
                    }
                  />
                )}
              </FormField>
              <FormField form={form} name="destination" label="Dropoff">
                {(field) => (
                  <AddressInput
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Dropoff address"
                    onPlace={(place) =>
                      setCoords((prev) => ({
                        ...prev,
                        destinationLat: place?.lat,
                        destinationLong: place?.lng,
                      }))
                    }
                  />
                )}
              </FormField>
            </Stack>
            <Stack gap={4}>
              <Text as="h2" variant="h2">
                Vehicle
              </Text>
              <Grid cols={2} gap={4}>
                <FormField form={form} name="vehicleMake" label="Make">
                  {(field) => (
                    <OptionSheet
                      title="Make"
                      searchable
                      placeholder="Select"
                      options={(makes ?? []).map((entry) => ({
                        value: entry.make,
                        label: entry.make,
                      }))}
                      value={field.value || undefined}
                      onValueChange={(value) => {
                        field.onChange(value);
                        form.setValue('vehicleModel', '');
                      }}
                    />
                  )}
                </FormField>
                <FormField form={form} name="vehicleModel" label="Model">
                  {(field) => (
                    <OptionSheet
                      title="Model"
                      searchable
                      placeholder="Select"
                      options={(models ?? []).map((entry) => ({
                        value: entry.model,
                        label: entry.model,
                      }))}
                      value={field.value || undefined}
                      onValueChange={field.onChange}
                    />
                  )}
                </FormField>
                <FormField form={form} name="vehicleYear" label="Year">
                  {(field) => (
                    <Input
                      {...field}
                      inputMode="numeric"
                      placeholder="Year of the vehicle"
                    />
                  )}
                </FormField>
                <FormField form={form} name="vehicleColor" label="Color">
                  {(field) => <Input {...field} placeholder="Paint color" />}
                </FormField>
                <FormField
                  form={form}
                  name="licensePlate"
                  label="License Plate"
                >
                  {(field) => (
                    <Input
                      {...field}
                      autoCapitalize="characters"
                      autoCorrect="off"
                      placeholder="Plate number"
                    />
                  )}
                </FormField>
                <FormField form={form} name="vin" label="VIN">
                  {(field) => (
                    <Input
                      {...field}
                      autoCapitalize="characters"
                      autoCorrect="off"
                      spellCheck={false}
                      maxLength={17}
                      placeholder="17 characters"
                    />
                  )}
                </FormField>
              </Grid>
            </Stack>
            <Stack gap={4}>
              <Text as="h2" variant="h2">
                Assign
              </Text>
              <FormField form={form} name="drivers" label="Operators">
                {(field) => (
                  <>
                    {operators.length ? (
                      // The list is the way in, as the Add an operator tile
                      // is when it is empty: a "Change operators" link
                      // under it was a second, smaller target for the
                      // same tap.
                      <Surface
                        as="button"
                        radius="lg"
                        padding="sm"
                        aria-label={['Change operators']
                          .concat(chosen.map((option) => option.name))
                          .join(', ')}
                        onClick={() => setPicking(true)}
                      >
                        <Stack gap={0}>
                          {chosen.map((option) => (
                            <MemberRow
                              key={option.id}
                              name={option.name}
                              detail={option.detail}
                            />
                          ))}
                        </Stack>
                      </Surface>
                    ) : (
                      <AddTile
                        width="block"
                        appearance="solid"
                        icon={Users}
                        onClick={() => setPicking(true)}
                      >
                        Add an operator
                      </AddTile>
                    )}
                    <OperatorPicker
                      open={picking}
                      onOpenChange={setPicking}
                      operators={operatorOptions}
                      selected={field.value}
                      onSelectedChange={field.onChange}
                    />
                  </>
                )}
              </FormField>
            </Stack>
          </Form>
        </BottomSheetContent>
      </BottomSheet>
      <RefusalUpsell
        refusal={refusal}
        onClose={() => setRefusal(null)}
        title="This month's calls are used"
      />
    </>
  );
}
